import React, { useEffect, useState } from "react"
import { Box } from "@mui/material"
import { AppFormDrawer, AppFormGrid, AppFormSection, AppMoreOptions } from "@components"
import { isAutoEuropeDrawerOpen, autoEuropeDrawerData, clearAutoEuropeSignals } from "Pages/AutoEuropeTool/Page/@signals"
import AutoEuropeDrawerFooter from "Pages/AutoEuropeTool/Page/@components/Footer"
import AutoEuropeName from "Pages/AutoEuropeTool/Page/@components/Name"
import AutoEuropeMinimumValueField from "Pages/AutoEuropeTool/Page/@components/MinimumValueField"
import AutoEuropeSeasonSetField from "Pages/AutoEuropeTool/Page/@components/SeasonSetField"
import AutoEuropeDurations from "Pages/AutoEuropeTool/Page/@components/DurationRange"
import AutoEuropeUndercut from "Pages/AutoEuropeTool/Page/@components/Undercut"
import AutoEuropeAgencyId from "Pages/AutoEuropeTool/Page/@components/AgencyId"
import AutoEuropeAgentId from "Pages/AutoEuropeTool/Page/@components/AgentId"
import AutoEuropeReports from "Pages/AutoEuropeTool/Page/@components/Reports"
import AutoEuropeExcludeOffices from "Pages/AutoEuropeTool/Page/@components/ExcludeOffices"
import AutoEuropeExcludeWebs from "Pages/AutoEuropeTool/Page/@components/ExcludeWebs"
import AutoEuropeExcludeWebSuppliers from "Pages/AutoEuropeTool/Page/@components/ExcludeWebSuppliers"
import AutoEuropeExcludeProviders from "Pages/AutoEuropeTool/Page/@components/ExcludeProviders"
import AutoEuropeFilterCategories from "Pages/AutoEuropeTool/Page/@components/FilterCategories"
import AutoEuropeFilterAcriss from "Pages/AutoEuropeTool/Page/@components/FilterAcriss"
import AutoEuropeInsurance, { AutoEuropeInsuranceSwitch } from "Pages/AutoEuropeTool/Page/@components/Insurance"
import AutoEuropeCompareBy from "Pages/AutoEuropeTool/Page/@components/CompareBy"
import AutoEuropeTargetPosition from "Pages/AutoEuropeTool/Page/@components/TargetPosition"

function hasFilterData() {
	const d = autoEuropeDrawerData.peek()
	const hasIds = (f) => Array.isArray(f) && f.length > 0
	return hasIds(d.filterGroupOffices) || hasIds(d.filterGroupSites) || hasIds(d.filterGroupWebSuppliers) || hasIds(d.filterGroupProviders) || hasIds(d.filterCategories) || hasIds(d.filterAcriss)
}

export default function AutoEuropeDrawer({ onSubmit, isLoading }) {
	const isEdit = !!autoEuropeDrawerData.peek().id
	const isOpen = isAutoEuropeDrawerOpen.value
	const compareByCategory = autoEuropeDrawerData.value?.compareByCategory ?? false
	const [filtersOpen, setFiltersOpen] = useState(false)

	useEffect(() => {
		if (isOpen) setFiltersOpen(hasFilterData())
	}, [isOpen])

	return <AppFormDrawer
		icon={isEdit ? 'pen' : 'file-plus'}
		title={isEdit ? 'Edit pricing tool' : 'Create AutoEurope Pricing Tool'}
		subtitle={isEdit ? autoEuropeDrawerData.peek().name : 'New pricing tool'}
		width="40%"
		variant="persistent"
		isOpen={isAutoEuropeDrawerOpen.value}
		controller={clearAutoEuropeSignals}
		footer={<AutoEuropeDrawerFooter onSubmit={onSubmit} isLoading={isLoading}/>}
	>
		<AppFormSection icon="tag" title="General">
			<AppFormGrid>
				<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeName/></Box>
			</AppFormGrid>
		</AppFormSection>

		<AppFormSection icon="percent" title="Price rules">
			<AppFormGrid>
				<Box sx={{ gridColumn: 'span 6' }}>
					<AutoEuropeMinimumValueField field="minimumMargin" label="Minimum Margin" placeholder="0" startIcon="percentage" max={100}/>
				</Box>
				<Box sx={{ gridColumn: 'span 6' }}>
					<AutoEuropeMinimumValueField field="minimumProfit" label="Minimum Profit" placeholder="0.00" startIcon="money-bill" max={500}/>
				</Box>
				<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeUndercut/></Box>
				<Box sx={{ gridColumn: 'span 6' }}><AutoEuropeCompareBy/></Box>
				<Box sx={{ gridColumn: 'span 6', display: 'flex', alignItems: 'flex-end', alignSelf: 'stretch' }}><AutoEuropeTargetPosition/></Box>
			</AppFormGrid>
		</AppFormSection>

		<AppFormSection icon="calendar-days" title="Dates">
			<AppFormGrid>
				<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeSeasonSetField/></Box>
				<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeDurations/></Box>
			</AppFormGrid>
		</AppFormSection>

		<AppFormSection icon="id-card" title="AutoEurope IDs">
			<AppFormGrid>
				<Box sx={{ gridColumn: 'span 6' }}><AutoEuropeAgencyId/></Box>
				<Box sx={{ gridColumn: 'span 6' }}><AutoEuropeAgentId/></Box>
			</AppFormGrid>
		</AppFormSection>

		<AppFormSection icon="file-lines" title="Reports">
			<AutoEuropeReports/>
		</AppFormSection>

		<AppFormSection icon="shield-check" title="Insurance" action={<AutoEuropeInsuranceSwitch/>}>
			{!!autoEuropeDrawerData.value?.insurance && <AutoEuropeInsurance/>}
		</AppFormSection>

		<AppFormSection icon="filter" title="Filters" hint="optional">
			<AppMoreOptions open={filtersOpen} onToggle={() => setFiltersOpen(open => !open)} label="Optional filters">
				<AppFormGrid>
					<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeExcludeOffices/></Box>
					<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeExcludeWebs/></Box>
					<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeExcludeWebSuppliers/></Box>
					<Box sx={{ gridColumn: 'span 12' }}><AutoEuropeExcludeProviders/></Box>
					<Box sx={{ gridColumn: 'span 12' }}>{compareByCategory ? <AutoEuropeFilterCategories/> : <AutoEuropeFilterAcriss/>}</Box>
				</AppFormGrid>
			</AppMoreOptions>
		</AppFormSection>
	</AppFormDrawer>
}
